import {useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  awardPointsToggleSchema,
  hasDisplayDateToggleSchema,
  joinPerfectStoreToggleSchema
} from '../PerfectStoreSetUp/schemas';
import ReviewField from './ReviewField';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {downloadCsvFromBlobUrl} from '@/helpers/blobCsvDownload';
import {Constants} from '@/utils/constants/commonConstants';
import {DATE_FORMAT_YYYYMMDD} from '@/utils/constants/globalConstants';
import {getFormattedDate} from '@/utils/commons';
import {getStoreTargetFileName, toYesNo} from './reviewHelpers';

const getFieldRowClassName = (columnCount, extraClassName = '') => {
  const base = 'perfect-store-review-field-row';
  const cols = columnCount === 3 ? `${base} ${base}--3` : base;
  return `${cols} ${extraClassName}`.trim();
};

const PerfectStoreSetupReview = ({formData = {}}) => {
  const {downloadFileOnBlob} = useAzureBlob();
  const fileName = getStoreTargetFileName(formData);
  const showRewardDate = !formData?.awardPointsWhenTargetMet;
  const activeAndDisplaySame = !formData?.hasDisplayDate;
  const displayStartDate = activeAndDisplaySame
    ? formData?.activeStartDate
    : formData?.displayStartDate;
  const displayEndDate = activeAndDisplaySame
    ? formData?.activeEndDate
    : formData?.displayEndDate;
  // Always 3 columns so Display Start/End line up under Active End / Points Delivery
  const availabilityColumns = 3;

  const handleFileDownload = useCallback(() => {
    void downloadCsvFromBlobUrl({
      fileUrl: formData?.storeTargetFileUrl,
      templateType: Constants.PerfectStore,
      downloadFileOnBlob
    });
  }, [formData?.storeTargetFileUrl, downloadFileOnBlob]);

  return (
    <div
      className='flex flex-col gap-6'
      data-testid='perfect-store-setup-review'>
      <div className='ml-4'>
        <p className='review-section-label mb-3'>Perfect Store Setup</p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <div className={getFieldRowClassName(2)}>
          <ReviewField label='Target Type' value={formData?.targetType} />
          <ReviewField label='Name' value={formData?.name} />
        </div>
      </div>

      <div className='ml-4'>
        <p className='review-section-label mb-3'>Store Target</p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <div>
          <div className='review_subtitle'>File</div>
          {formData?.storeTargetFileUrl ? (
            <button
              type='button'
              className='review-file-link'
              onClick={handleFileDownload}
              data-testid='perfect-store-setup-file-link'>
              {fileName || 'Download file'}
            </button>
          ) : (
            <span
              className='review_title'
              data-testid='perfect-store-setup-file-empty'>
              {fileName || '-'}
            </span>
          )}
        </div>
      </div>

      <div className='ml-4'>
        <p className='review-section-label mb-3'>Availability</p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <div className={getFieldRowClassName(availabilityColumns)}>
          <ReviewField
            label='Active Start Date'
            value={getFormattedDate(
              formData?.activeStartDate,
              false,
              '-',
              DATE_FORMAT_YYYYMMDD
            )}
          />
          <ReviewField
            label='Active End Date'
            value={getFormattedDate(
              formData?.activeEndDate,
              false,
              '-',
              DATE_FORMAT_YYYYMMDD
            )}
          />
          {showRewardDate ? (
            <ReviewField
              label='Point Delivery Date'
              value={getFormattedDate(
                formData?.rewardDate,
                false,
                '-',
                DATE_FORMAT_YYYYMMDD
              )}
            />
          ) : (
            <div aria-hidden='true' />
          )}
        </div>
        <div className={getFieldRowClassName(availabilityColumns, 'mt-4')}>
          <ReviewField
            label={hasDisplayDateToggleSchema.label}
            value={toYesNo(activeAndDisplaySame)}
          />
          <ReviewField
            label='Display Start Date'
            value={getFormattedDate(
              displayStartDate,
              false,
              '-',
              DATE_FORMAT_YYYYMMDD
            )}
          />
          <ReviewField
            label='Display End Date'
            value={getFormattedDate(
              displayEndDate,
              false,
              '-',
              DATE_FORMAT_YYYYMMDD
            )}
          />
        </div>
        <div className={getFieldRowClassName(2, 'mt-4')}>
          <ReviewField
            label={awardPointsToggleSchema.label}
            value={toYesNo(formData?.awardPointsWhenTargetMet)}
          />
        </div>
      </div>
      <div className='ml-4'>
        <p className='review-section-label mb-3'>Additional Options</p>
        <hr className='mb-2 border-[color:var(--color-border-divider)]' />
        <ReviewField
          label={joinPerfectStoreToggleSchema.label}
          value={toYesNo(formData?.requiredUserToJoin)}
        />
      </div>
    </div>
  );
};

PerfectStoreSetupReview.propTypes = {
  formData: PropTypes.object
};

export default PerfectStoreSetupReview;
